import { defineStore } from "pinia"
import { useUserStore } from "@/store/user-store"
import { asDateTime, yearFromIso } from "@common/utilities/time-utils"
import { useListPendingCompanyBenefitEnrollments } from "@/composables/benefits-enrollment"
import {
  getEffectiveWaivedDependentsIds,
  getVpcBenefitElectionFromPendingEnrollment,
  isChildDependentOverAgeLimit,
  isMedicareEligible,
  isPlanSelected
} from "@/modules/enrollment-new/enrollment-flow-helpers"
import { DateTime, Settings } from "luxon"
import {
  BenefitElectionDependent,
  BenefitElectionSelectionsCostSummary,
  BenefitElectionsPackage,
  BenefitElectionsPackageConfirmation,
  BenefitFamilyStatusEnum,
  CompanyPendingElections,
  EnrollmentBenefitStep,
  NonVpcBenefitElection,
  NonVpcElectionsWithYear,
  VpcBenefitElection
} from "@/modules/enrollment-new/enrollment-flow-types"
import {
  BenefitDependentTypeEnum,
  BenefitTypeEnum,
  CompanyBenefitPlanDTO,
  CompanyBenefitPlanTierCostDTO,
  LegalDependentWithDetails,
  MarketplacePlanDTO,
  PendingCompanyBenefitEnrollmentDTO
} from "@vitable/asclepius-api-client-ts"
import {
  displayBenefitName,
  ENROLLMENT_BENEFIT_STEP_ORDER,
  ICHRA_BENEFIT_CODES,
  VPC_STEP
} from "@common/utilities/benefit-utils"
import { groupElectionsByYear } from "@/modules/enrollment-new/enrollment-segments"
import { groupPendingElections, type PendingElectionsGroup } from "@/modules/enrollment-new/enrollment-groups"
import type { EnrollmentRun, JustSignedEnrollment, SignedEnrollment } from "@/modules/enrollment-new/enrollment-run"
import {
  buildSummaryRows,
  type BenefitSummaryRowData,
  type BenefitSummaryRowStatus
} from "@/modules/enrollment-new/pages/enrollment-flow/pages/sign-forms/build-summary-rows"

export type SummaryDetailData =
  | {
      kind: "internal-breakdown"
      employeeCostCents: number
      dependentCostCents: number
      enrolledDependentsCount: number
    }
  | { kind: "family-shape"; enrolledDependentsCount: number }
  | null

export interface NonVpcElectionSummary {
  planName: string | null
  /**
   * Medicare-specific full label combining the display benefit name with
   * "+ Medicare" (e.g. "Employer health stipend + Medicare" for ICHRA,
   * "Medical Benefit + Medicare" otherwise). `null` for non-Medicare.
   */
  medicareWithBenefitName: string | null
  status: BenefitSummaryRowStatus
  monthlyCostCents: number | null
  detailData: SummaryDetailData
}

export function computeNonVpcElectionSummary(
  election: NonVpcBenefitElection,
  dependents: Array<BenefitElectionDependent>
): NonVpcElectionSummary {
  const selection = election.selection
  const waivedDependentsIds = getEffectiveWaivedDependentsIds(election, dependents)

  if (selection?.type === "internal") {
    const employeeCostCents = selection.tierCost.cost ?? 0
    const dependentCostCents = selection.tierCost.cost_per_dependent ?? 0
    const enrolledDependentsCount = dependents.filter((d) => !waivedDependentsIds.includes(d.legalDependentId)).length
    return {
      planName: selection.benefitPlan.name,
      medicareWithBenefitName: null,
      status: "selected",
      monthlyCostCents: employeeCostCents + dependentCostCents * enrolledDependentsCount,
      detailData: { kind: "internal-breakdown", employeeCostCents, dependentCostCents, enrolledDependentsCount }
    }
  }

  if (selection?.type === "external") {
    const legacyPremiumDollars = (selection.externalPlan as MarketplacePlanDTO & { premium?: number }).premium
    const planPremiumInCentsFromDto = selection.externalPlan.premium_in_cents
    const planPremiumInCents =
      typeof planPremiumInCentsFromDto === "number" && Number.isFinite(planPremiumInCentsFromDto)
        ? planPremiumInCentsFromDto
        : typeof legacyPremiumDollars === "number" && Number.isFinite(legacyPremiumDollars)
          ? Math.trunc(legacyPremiumDollars * 100)
          : 0
    const employerAllowanceInCents = selection.employerAllowanceInCents ?? 0
    const enrolledDependentsCount = dependents.filter((d) => !waivedDependentsIds.includes(d.legalDependentId)).length
    return {
      planName: selection.externalPlan.name,
      medicareWithBenefitName: null,
      status: "selected",
      monthlyCostCents: Math.max(0, planPremiumInCents - employerAllowanceInCents),
      detailData: { kind: "family-shape", enrolledDependentsCount }
    }
  }

  if (selection?.type === "medicare") {
    return {
      planName: "Medicare",
      medicareWithBenefitName: `${displayBenefitName(election.enrollment)} + Medicare`,
      status: "selected",
      monthlyCostCents: 0,
      detailData: null
    }
  }

  // null = waived, undefined = unresolved.
  return {
    planName: null,
    medicareWithBenefitName: null,
    status: "waived",
    monthlyCostCents: null,
    detailData: null
  }
}

function sortUngroupedByEnrollmentId(elections: Array<NonVpcBenefitElection>): Array<NonVpcBenefitElection> {
  return [...elections].sort((a, b) => a.enrollment.id.localeCompare(b.enrollment.id))
}

type PersistedCompanyPendingElections = Partial<CompanyPendingElections> & { vpcElection?: VpcBenefitElection }

/** Persisted state written before flows were split by plan year holds a single `vpcElection` per company. */
export function migratePersistedPendingElections(
  persisted: Record<string, PersistedCompanyPendingElections> | null | undefined
): Record<string, CompanyPendingElections> {
  const migrated: Record<string, CompanyPendingElections> = {}
  for (const [companyId, elections] of Object.entries(persisted ?? {})) {
    migrated[companyId] = {
      vpcElections: elections.vpcElections ?? (elections.vpcElection ? [elections.vpcElection] : []),
      nonVpcElections: elections.nonVpcElections ?? []
    }
  }
  return migrated
}

export interface EnrollmentStore {
  pendingElectionsPackagesByCompany: Record<string, CompanyPendingElections>
  activeFlowCompanyId: Optional<string> // undefined = no enrollment sequence in-progress
  // null = the company's earliest group
  activeFlowPlanYearStartYear: number | null
  activeFlowHasConfirmedAccount: boolean
  activeFlowHasScheduledMedicareCall: boolean
  // Company of the last started flow, so a scheduled Medicare call carries only into that company's later groups.
  lastStartedFlowCompanyId: Optional<string>
  activeFlowDependents: Array<BenefitElectionDependent>
  activeFlowCountyFipsCode: Optional<string>
  activeFlowCountyName: Optional<string>
  // One-shot: set on sign-and-finish, consumed when the next page mounts its prompts, so reloads do not re-open them.
  justSignedEnrollment: JustSignedEnrollment | null
  enrollmentRunsByCompany: Record<string, EnrollmentRun>
}

export const useEnrollmentStore = defineStore("enrollmentNewStore", {
  persist: {
    afterRestore: ({ store }) => {
      store.$state.pendingElectionsPackagesByCompany = migratePersistedPendingElections(
        store.$state.pendingElectionsPackagesByCompany
      )
      delete store.$state.pendingCsatPrompt
      delete store.$state.pendingPostEnrollmentPrompt
    }
  },
  state: (): EnrollmentStore => {
    return {
      pendingElectionsPackagesByCompany: {},
      activeFlowCompanyId: undefined,
      activeFlowPlanYearStartYear: null,
      activeFlowHasConfirmedAccount: false,
      activeFlowHasScheduledMedicareCall: false,
      lastStartedFlowCompanyId: undefined,
      activeFlowDependents: [],
      activeFlowCountyFipsCode: undefined,
      activeFlowCountyName: undefined,
      justSignedEnrollment: null,
      enrollmentRunsByCompany: {}
    }
  },
  getters: {
    pendingElectionGroups(): Array<PendingElectionsGroup> {
      return Object.entries(this.pendingElectionsPackagesByCompany).flatMap(([companyId, elections]) =>
        groupPendingElections(companyId, elections)
      )
    },
    pendingElectionGroupsForCompany(): (companyId: string) => Array<PendingElectionsGroup> {
      return (companyId) => this.pendingElectionGroups.filter((group) => group.companyId === companyId)
    },
    activeFlowGroup(): Optional<PendingElectionsGroup> {
      const companyId = this.activeFlowCompanyId
      if (!companyId) return undefined
      const groups = this.pendingElectionGroupsForCompany(companyId)
      if (this.activeFlowPlanYearStartYear === null) return groups[0]
      return groups.find((group) => group.planYearStartYear === this.activeFlowPlanYearStartYear)
    },
    // Election objects are shared with `pendingElectionsPackagesByCompany`, so mutating them updates the store.
    activeFlowElectionsPackage(): Optional<BenefitElectionsPackage> {
      const group = this.activeFlowGroup
      if (!group) return undefined
      return { vpcElection: group.vpcElection, nonVpcElections: group.nonVpcElections }
    },
    /** The VPC election this flow signs for, included whenever a selected medical plan locks it in. */
    activeFlowVpcElectionToSubmit(): Optional<VpcBenefitElection> {
      const vpcElection = this.activeFlowGroup?.vpcElection
      if (vpcElection?.waived && this.vpcAutoIncludedIn) return { ...vpcElection, waived: false }
      return vpcElection
    },
    activeFlowCompletionDeadline(): Optional<string> {
      const pendingElectionsPackage = this.activeFlowElectionsPackage

      if (!pendingElectionsPackage) {
        return undefined
      }

      // ASSUMPTION:
      // VPC Benefits will not have a strict open enrollment period nor renewal.
      // Target date will always be the deadline of the soonest non-VPC Benefit.
      if (pendingElectionsPackage.nonVpcElections.length === 0) {
        return undefined
      }

      const possibleTargetDates: Array<DateTime> = []

      for (const nonVpcElection of pendingElectionsPackage.nonVpcElections) {
        const enrollmentWindowEnd: string | null = nonVpcElection.enrollment.enrollment_window_end
        if (enrollmentWindowEnd) {
          possibleTargetDates.push(asDateTime(enrollmentWindowEnd))
        }
      }

      possibleTargetDates.sort((a, b) => {
        if (a < b) {
          return -1
        } else if (a > b) {
          return 1
        }
        return 0
      })

      // Sort is ascending and the surrounding comment promises the soonest
      // deadline; `shift()` returns the earliest, `pop()` would return the latest.
      const targetDate: DateTime | undefined = possibleTargetDates.shift()

      if (!targetDate) {
        return undefined
      }

      return targetDate.setLocale(Settings.defaultLocale).toLocaleString({ month: "short", day: "2-digit" })
    },
    activeFlowElectionsVpcOnlyIn(): boolean {
      return (this.activeFlowElectionsPackage?.nonVpcElections?.length ?? 0) === 0
    },
    activeFlowElectionsHasIchraIn(): boolean {
      return !!this.activeFlowElectionsPackage?.nonVpcElections?.some((e: NonVpcBenefitElection) =>
        ICHRA_BENEFIT_CODES.includes(e.enrollment.benefit_code)
      )
    },
    activeFlowNonVpcElectionsByYear(): Array<NonVpcElectionsWithYear> {
      return groupElectionsByYear(this.activeFlowElectionsPackage?.nonVpcElections ?? [])
    },
    activeFlowAvailableNonVpcElectionsYears(): Array<number> {
      return this.activeFlowNonVpcElectionsByYear.map((group) => group.year)
    },
    _activeFlowElectionsByType(): Record<string, Array<NonVpcBenefitElection>> {
      const result: Record<string, Array<NonVpcBenefitElection>> = {}
      for (const e of this.activeFlowElectionsPackage?.nonVpcElections ?? []) {
        const type = e.enrollment.benefit_type
        if (!result[type]) result[type] = []
        result[type].push(e)
      }
      return result
    },
    activeFlowMedicalElections(): Array<NonVpcBenefitElection> {
      return this._activeFlowElectionsByType[BenefitTypeEnum.Medical] ?? []
    },
    activeFlowDentalElections(): Array<NonVpcBenefitElection> {
      return this._activeFlowElectionsByType[BenefitTypeEnum.Dental] ?? []
    },
    activeFlowVisionElections(): Array<NonVpcBenefitElection> {
      return this._activeFlowElectionsByType[BenefitTypeEnum.Vision] ?? []
    },
    activeFlowMedicalElectionsByYear(): Array<NonVpcElectionsWithYear> {
      return groupElectionsByYear(this.activeFlowMedicalElections)
    },
    vpcAutoIncludedIn(): boolean {
      return this.activeFlowMedicalElections.some(
        (election) => election.selection !== null && election.selection !== undefined
      )
    },
    activeFlowDentalElectionsByYear(): Array<NonVpcElectionsWithYear> {
      return groupElectionsByYear(this.activeFlowDentalElections)
    },
    activeFlowVisionElectionsByYear(): Array<NonVpcElectionsWithYear> {
      return groupElectionsByYear(this.activeFlowVisionElections)
    },
    activeFlowAvailableBenefitTypes(): Array<EnrollmentBenefitStep> {
      const available: Array<EnrollmentBenefitStep> = []
      for (const step of ENROLLMENT_BENEFIT_STEP_ORDER) {
        if (step === VPC_STEP) {
          if (this.activeFlowElectionsPackage?.vpcElection) {
            available.push(step)
          }
        } else {
          if ((this._activeFlowElectionsByType[step] ?? []).length > 0) {
            available.push(step)
          }
        }
      }
      return available
    },
    activeFlowElectionsByBenefitTypeAndYear(): (benefitType: BenefitTypeEnum) => Array<NonVpcElectionsWithYear> {
      return (benefitType: BenefitTypeEnum) => {
        return groupElectionsByYear(this._activeFlowElectionsByType[benefitType] ?? [])
      }
    },
    activeFlowPrimaryEffectiveDate(): (benefitType: BenefitTypeEnum, year: string) => string | undefined {
      return (benefitType, year) => {
        const elections = this._activeFlowElectionsByType[benefitType] ?? []
        const election = elections.find((e) => e.enrollment.plan_year_coverage_start.startsWith(year))
        return election?.enrollment.expected_coverage_effective_start_date ?? undefined
      }
    },
    activeFlowAllNonVpcElectionsElectedIn(): boolean {
      const nonVpcElections = this.activeFlowElectionsPackage?.nonVpcElections ?? []
      return nonVpcElections.every(isPlanSelected)
    },
    activeFlowElectionsConfirmation(): BenefitElectionsPackageConfirmation {
      const selected: BenefitElectionsPackageConfirmation["selected"] = []
      const waived: BenefitElectionsPackageConfirmation["waived"] = []
      let selectedIncludesNonVpcIn = false
      let waivedIncludesNonVpcIn = false

      const electionsPackage = this.activeFlowElectionsPackage

      const vpcBenefitElection = this.activeFlowVpcElectionToSubmit
      if (vpcBenefitElection) {
        const planRef = {
          benefitPlanName: vpcBenefitElection.vpcBenefitPlan.name,
          sbcFileUrl: vpcBenefitElection.vpcBenefitPlan.benefit_summary_file_url
        }
        if (vpcBenefitElection.waived) {
          waived.push({ benefitName: vpcBenefitElection.vpcBenefitPlan.name, plans: [planRef] })
        } else {
          selected.push(planRef)
        }
      }

      for (const nonVpcBenefitElection of electionsPackage?.nonVpcElections ?? []) {
        const selection = nonVpcBenefitElection.selection
        if (selection?.type == "internal") {
          selectedIncludesNonVpcIn = true
          selected.push({
            benefitPlanName: selection.benefitPlan.name,
            sbcFileUrl: selection.benefitPlan.benefit_summary_file_url
          })
        } else if (selection?.type == "external") {
          selectedIncludesNonVpcIn = true
          selected.push({
            benefitPlanName: selection.externalPlan.name,
            sbcFileUrl: selection.externalPlan.benefits_summary_url
          })
        } else if (selection?.type == "medicare") {
          selectedIncludesNonVpcIn = true
          selected.push({
            benefitPlanName: `${displayBenefitName(nonVpcBenefitElection.enrollment)} + Medicare`,
            sbcFileUrl: null
          })
        } else {
          waivedIncludesNonVpcIn = true
          waived.push({
            benefitName: displayBenefitName(nonVpcBenefitElection.enrollment),
            plans: nonVpcBenefitElection.enrollment.benefit_plans.map((bp) => ({
              benefitPlanName: bp.name,
              sbcFileUrl: bp.benefit_summary_file_url
            }))
          })
        }
      }

      return { selected, waived, selectedIncludesNonVpcIn, waivedIncludesNonVpcIn }
    },
    _orderedElectionsByStep(): Partial<Record<EnrollmentBenefitStep, Array<NonVpcBenefitElection>>> {
      const result: Partial<Record<EnrollmentBenefitStep, Array<NonVpcBenefitElection>>> = {}
      for (const [step, typeElections] of Object.entries(this._activeFlowElectionsByType) as Array<
        [BenefitTypeEnum, Array<NonVpcBenefitElection>]
      >) {
        const grouped = groupElectionsByYear(typeElections).flatMap((g) => g.elections)
        const groupedIds = new Set(grouped.map((e) => e.enrollment.id))
        const ungrouped = sortUngroupedByEnrollmentId(typeElections.filter((e) => !groupedIds.has(e.enrollment.id)))
        result[step] = [...grouped, ...ungrouped]
      }
      return result
    },
    // Memoized per-election summaries — re-derived once when dependents or the
    // elections package change. Both the sticky-footer cost summary and the
    // sign-forms summary table read from this map instead of recomputing.
    _summariesByElectionId(): Record<string, NonVpcElectionSummary> {
      const result: Record<string, NonVpcElectionSummary> = {}
      for (const election of this.activeFlowElectionsPackage?.nonVpcElections ?? []) {
        result[election.enrollment.id] = computeNonVpcElectionSummary(election, this.activeFlowDependents)
      }
      return result
    },
    activeFlowElectionsCostSummary(): BenefitElectionSelectionsCostSummary {
      const electionsPackage = this.activeFlowElectionsPackage
      if (!electionsPackage) return { totalCost: 0, selections: [] }

      const costItems: Array<{ label: string; cost: number; waived?: boolean }> = []
      let runningTotalCostInCents = 0
      const hasMultipleYears = this.activeFlowNonVpcElectionsByYear.length > 1

      const withYearPrefix = (name: string, start: string | null | undefined): string => {
        if (!hasMultipleYears) return name
        const year = yearFromIso(start)
        return year !== undefined ? `${year}: ${name}` : name
      }

      // Iterate in canonical display order: Medical → VPC → Dental → Vision.
      for (const step of ENROLLMENT_BENEFIT_STEP_ORDER) {
        if (step === VPC_STEP) {
          const vpcElection = this.activeFlowVpcElectionToSubmit
          if (!vpcElection) continue
          if (vpcElection.waived) {
            costItems.push({ label: "Vitable Primary Care", cost: 0, waived: true })
            continue
          }
          const vpcEmployeeCostInCents = vpcElection.vpcTierCost.cost ?? 0
          const vpcDependentCostInCents = vpcElection.vpcTierCost.cost_per_dependent ?? 0
          const vpcWaivedDependentsIds = vpcElection.waivedDependentsIds ?? []
          const vpcDependentsCount = this.activeFlowDependents.filter(
            (d) => !vpcWaivedDependentsIds.includes(d.legalDependentId)
          ).length
          const vpcTotalCostInCents = vpcEmployeeCostInCents + vpcDependentCostInCents * vpcDependentsCount
          runningTotalCostInCents += vpcTotalCostInCents
          costItems.push({ label: "Vitable Primary Care", cost: vpcTotalCostInCents / 100 })
          continue
        }

        for (const election of this._orderedElectionsByStep[step] ?? []) {
          const enrollment = election.enrollment
          const start = enrollment.plan_year_coverage_start
          const summary = this._summariesByElectionId[election.enrollment.id]

          if (summary.status === "waived") {
            continue
          }

          const baseLabel = summary.medicareWithBenefitName ?? summary.planName ?? enrollment.benefit_name
          const totalCostInCents = summary.monthlyCostCents ?? 0
          runningTotalCostInCents += totalCostInCents

          costItems.push({
            label: withYearPrefix(baseLabel, start),
            cost: totalCostInCents / 100
          })
        }
      }

      return { totalCost: runningTotalCostInCents / 100, selections: costItems }
    },
    activeFlowElectionsSummaryRows(): Array<BenefitSummaryRowData> {
      const electionsPackage = this.activeFlowElectionsPackage
      if (!electionsPackage) return []
      return buildSummaryRows({
        electionsPackage: { ...electionsPackage, vpcElection: this.activeFlowVpcElectionToSubmit },
        dependents: this.activeFlowDependents,
        availableBenefitTypes: this.activeFlowAvailableBenefitTypes,
        electionsByType: this._activeFlowElectionsByType,
        hasMultipleYears: this.activeFlowNonVpcElectionsByYear.length > 1,
        orderedElectionsByStep: this._orderedElectionsByStep,
        summariesByElectionId: this._summariesByElectionId,
        medicareEligibleIn: isMedicareEligible(useUserStore().member)
      })
    }
  },
  actions: {
    async _fetchPendingCompanyBenefitEnrollments(): Promise<Array<PendingCompanyBenefitEnrollmentDTO>> {
      const memberId: string = useUserStore().memberId!
      const { pendingCompanyBenefitEnrollments, listPendingCompanyBenefitEnrollments } =
        useListPendingCompanyBenefitEnrollments()
      await listPendingCompanyBenefitEnrollments(memberId)
      return pendingCompanyBenefitEnrollments.value
    },

    async loadAllPendingCompanyBenefitElections(): Promise<void> {
      // Convert each pending enrollment into a benefit election sorted by respective company
      const fetchedEnrollments = await this._fetchPendingCompanyBenefitEnrollments()
      const fetchedEnrollmentIds = new Set(fetchedEnrollments.map((enrollment) => enrollment.id))

      for (const pendingEnrollment of fetchedEnrollments) {
        const companyId = pendingEnrollment.company_id

        // Sort the benefit plans by level (min cost first)
        pendingEnrollment.benefit_plans.sort((a, b) => {
          if (a.level < b.level) {
            return -1
          } else if (a.level > b.level) {
            return 1
          }
          return 0
        })

        const companyElections: CompanyPendingElections = this.pendingElectionsPackagesByCompany[companyId] || {
          vpcElections: [],
          nonVpcElections: []
        }

        const vpcBenefitElection = getVpcBenefitElectionFromPendingEnrollment(pendingEnrollment)

        if (vpcBenefitElection) {
          // This enrollment is for a VPC Benefit
          const existingIndex = companyElections.vpcElections.findIndex((e) => e.enrollment.id == pendingEnrollment.id)
          const existingVpcElection = companyElections.vpcElections[existingIndex]
          if (existingIndex === -1 || !existingVpcElection) {
            companyElections.vpcElections.push(vpcBenefitElection)
          } else {
            // Keep the member's decision across reloads, like non-VPC selections.
            companyElections.vpcElections[existingIndex] = {
              ...vpcBenefitElection,
              waived: existingVpcElection.waived ?? false,
              waivedDependentsIds: existingVpcElection.waivedDependentsIds ?? []
            }
          }
        } else {
          // This enrollment is for a Non-VPC Benefit
          const existingNonVpcElection = companyElections.nonVpcElections.find(
            (e) => e.enrollment.id == pendingEnrollment.id
          )

          if (existingNonVpcElection) {
            const priorWaivedIds = this.getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(existingNonVpcElection)
            existingNonVpcElection.enrollment = pendingEnrollment
            this.refreshNonVpcElectionSelectionIfWaiversChanged(existingNonVpcElection, priorWaivedIds)
          } else {
            companyElections.nonVpcElections.push({
              enrollment: pendingEnrollment,
              selection: undefined,
              waivedDependentsIds: []
            })
          }
        }

        this.pendingElectionsPackagesByCompany[companyId] = companyElections
      }

      // Drop elections the API no longer returns (e.g. finished in another session) so they are never resubmitted.
      for (const companyId of Object.keys(this.pendingElectionsPackagesByCompany)) {
        this._removeCompanyElections(companyId, (enrollmentId) => !fetchedEnrollmentIds.has(enrollmentId))
      }
    },

    _removeCompanyElections(companyId: string, shouldRemove: (enrollmentId: string) => boolean): void {
      const companyElections = this.pendingElectionsPackagesByCompany[companyId]
      if (!companyElections) return
      companyElections.vpcElections = companyElections.vpcElections.filter((e) => !shouldRemove(e.enrollment.id))
      companyElections.nonVpcElections = companyElections.nonVpcElections.filter((e) => !shouldRemove(e.enrollment.id))
      if (companyElections.vpcElections.length === 0 && companyElections.nonVpcElections.length === 0) {
        delete this.pendingElectionsPackagesByCompany[companyId]
      }
    },

    isActiveEnrollmentFlowStillValid(): boolean {
      return !!this.activeFlowGroup
    },

    startEnrollmentFlow(companyId: string, options: { planYearStartYear?: number | null } = {}): void {
      this.stopEnrollmentFlow()
      if (companyId !== this.lastStartedFlowCompanyId) this.activeFlowHasScheduledMedicareCall = false
      this.lastStartedFlowCompanyId = companyId
      this.activeFlowCompanyId = companyId
      this.activeFlowPlanYearStartYear =
        options.planYearStartYear ?? this.pendingElectionGroupsForCompany(companyId)[0]?.planYearStartYear ?? null
    },

    stopEnrollmentFlow(): void {
      this.activeFlowCompanyId = undefined
      this.activeFlowPlanYearStartYear = null
      this.activeFlowHasConfirmedAccount = false
      this.activeFlowDependents = []
      this.activeFlowCountyFipsCode = undefined
      this.activeFlowCountyName = undefined
    },

    completeEnrollmentFlow(): void {
      const companyId = this.activeFlowCompanyId
      const electionsPackage = this.activeFlowElectionsPackage
      if (companyId && electionsPackage) {
        const vpcElection = this.activeFlowVpcElectionToSubmit
        const finishedEnrollmentIds = new Set(
          [...electionsPackage.nonVpcElections, ...(vpcElection ? [vpcElection] : [])].map((e) => e.enrollment.id)
        )
        this._removeCompanyElections(companyId, (enrollmentId) => finishedEnrollmentIds.has(enrollmentId))
      }
      this.stopEnrollmentFlow()
      // A Medicare call already scheduled still applies to the company's remaining plan years.
      if (!companyId || !this.pendingElectionsPackagesByCompany[companyId]) {
        this.activeFlowHasScheduledMedicareCall = false
      }
    },

    setCounty(fipsCode: string | undefined, name: string | undefined): void {
      this.activeFlowCountyFipsCode = fipsCode
      this.activeFlowCountyName = name
    },

    setJustSignedEnrollment(justSigned: Omit<JustSignedEnrollment, "memberId">): void {
      this.justSignedEnrollment = { ...justSigned, memberId: useUserStore().memberId ?? "" }
    },

    takeJustSignedEnrollment(): JustSignedEnrollment | null {
      const justSigned = this.justSignedEnrollment
      this.justSignedEnrollment = null
      return justSigned?.memberId === (useUserStore().memberId ?? "") ? justSigned : null
    },

    _enrollmentRun(companyId: string): Optional<EnrollmentRun> {
      const run = this.enrollmentRunsByCompany[companyId]
      if (run && run.memberId !== (useUserStore().memberId ?? "")) {
        delete this.enrollmentRunsByCompany[companyId]
        return undefined
      }
      return run
    },

    recordSignedEnrollments(companyId: string, signedEnrollments: Array<SignedEnrollment>): EnrollmentRun {
      const run = this._enrollmentRun(companyId) ?? {
        memberId: useUserStore().memberId ?? "",
        signedEnrollments: [],
        vpcOnboardingOwed: false
      }
      this.enrollmentRunsByCompany[companyId] = {
        ...run,
        signedEnrollments: [...run.signedEnrollments, ...signedEnrollments]
      }
      return this.enrollmentRunsByCompany[companyId]
    },

    markVpcOnboardingOwed(companyId: string): void {
      const run = this._enrollmentRun(companyId)
      if (run) run.vpcOnboardingOwed = true
    },

    takeEnrollmentRun(companyId: string): Optional<EnrollmentRun> {
      const run = this._enrollmentRun(companyId)
      delete this.enrollmentRunsByCompany[companyId]
      return run
    },

    syncDependentsFromServer(serverDeps: LegalDependentWithDetails[]): void {
      const currentIds = new Set(this.activeFlowDependents.map((d) => d.legalDependentId))
      const serverIds = new Set(serverDeps.map((d) => d.legal_dependent_id))

      const compositionChanged = currentIds.size !== serverIds.size || [...serverIds].some((id) => !currentIds.has(id))
      const nonVpcElections = this.activeFlowElectionsPackage?.nonVpcElections ?? []
      const priorWaivedIdsByElection = nonVpcElections.map((election) =>
        this.getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(election)
      )

      this.activeFlowDependents = serverDeps.map((serverDep) => ({
        legalDependentId: serverDep.legal_dependent_id,
        dependentMemberId: serverDep.dependent_member_id,
        firstName: serverDep.first_name,
        lastName: serverDep.last_name,
        suffix: serverDep.suffix ?? undefined,
        dateOfBirth: serverDep.date_of_birth,
        sex: serverDep.sex ?? undefined,
        gender: serverDep.gender ?? undefined,
        email: serverDep.email ?? "",
        phone: serverDep.phone ?? "",
        type: serverDep.relationship,
        maskedSsn: serverDep.ssn_last_four ? `***-**-${serverDep.ssn_last_four}` : "",
        encryptedSsn: "",
        address: serverDep.address ?? undefined
      }))

      if (compositionChanged) {
        // Clean up stale waiver IDs that reference dependents no longer in the list
        this.activeFlowElectionsPackage?.nonVpcElections.forEach((election) => {
          election.waivedDependentsIds = election.waivedDependentsIds.filter((id) => serverIds.has(id))
        })
        const vpcElection = this.activeFlowElectionsPackage?.vpcElection
        if (vpcElection) {
          vpcElection.waivedDependentsIds = (vpcElection.waivedDependentsIds ?? []).filter((id) => serverIds.has(id))
        }

        this.updateNonVpcElectionSelectionsForDependentChanges()
      } else {
        nonVpcElections.forEach((election, index) =>
          this.refreshNonVpcElectionSelectionIfWaiversChanged(election, priorWaivedIdsByElection[index] ?? [])
        )
      }
    },

    refreshNonVpcElectionSelectionIfWaiversChanged(
      nonVpcBenefitElection: NonVpcBenefitElection,
      priorWaivedIds: ReadonlyArray<string>
    ): void {
      const waivedIds = this.getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(nonVpcBenefitElection)
      const unchanged =
        waivedIds.length === priorWaivedIds.length && waivedIds.every((id) => priorWaivedIds.includes(id))
      if (!unchanged) this.refreshNonVpcElectionSelectionForDependentChange(nonVpcBenefitElection)
    },

    refreshNonVpcElectionSelectionForDependentChange(nonVpcBenefitElection: NonVpcBenefitElection): void {
      const selection = nonVpcBenefitElection.selection
      if (selection?.type === "external") {
        nonVpcBenefitElection.selection = undefined
      } else if (selection?.type === "internal") {
        const tierCost = this.getValidTierCostForNonVpcBenefitElectionAndBenefitPlan(
          selection.benefitPlan,
          nonVpcBenefitElection
        )
        nonVpcBenefitElection.selection = tierCost ? { ...selection, tierCost } : undefined
      }
    },

    updateNonVpcElectionSelectionsForDependentChanges() {
      this.activeFlowElectionsPackage?.nonVpcElections.forEach((nonVpcElection) => {
        const selection = nonVpcElection.selection
        if (selection) {
          nonVpcElection.selection = undefined
        }
      })
    },

    getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(
      nonVpcBenefitElection: NonVpcBenefitElection
    ): Array<string> {
      return getEffectiveWaivedDependentsIds(nonVpcBenefitElection, this.activeFlowDependents)
    },

    getDependentsEnrolledInNonVpcBenefitElection(
      nonVpcBenefitElection: NonVpcBenefitElection
    ): Array<BenefitElectionDependent> {
      const waivedDependentsIds = this.getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(nonVpcBenefitElection)
      return this.activeFlowDependents.filter((d) => !waivedDependentsIds.includes(d.legalDependentId))
    },

    isDependentEnrolledInNonVpcBenefitElection(
      dependent: BenefitElectionDependent,
      nonVpcBenefitElection: NonVpcBenefitElection
    ): boolean {
      return !this.getEffectiveWaivedDependentsIdsForNonVpcBenefitElection(nonVpcBenefitElection).includes(
        dependent.legalDependentId
      )
    },

    electNonVpcBenefitElectionForDependent(
      dependent: BenefitElectionDependent,
      nonVpcBenefitElection: NonVpcBenefitElection,
      enroll: boolean
    ): void {
      if (enroll && isChildDependentOverAgeLimit(dependent, nonVpcBenefitElection.enrollment)) return
      const index = nonVpcBenefitElection.waivedDependentsIds.findIndex((id) => id === dependent.legalDependentId)
      const wasMembershipChanged = enroll ? index !== -1 : index === -1
      if (enroll) {
        if (index !== -1) {
          nonVpcBenefitElection.waivedDependentsIds.splice(index, 1)
        }
      } else {
        if (index === -1) {
          nonVpcBenefitElection.waivedDependentsIds.push(dependent.legalDependentId)
        }
      }
      if (wasMembershipChanged) this.refreshNonVpcElectionSelectionForDependentChange(nonVpcBenefitElection)
    },

    getValidFamilyStatusForNonVpcBenefitElection(
      nonVpcBenefitElection: NonVpcBenefitElection
    ): BenefitFamilyStatusEnum {
      const enrolledDependents = this.getDependentsEnrolledInNonVpcBenefitElection(nonVpcBenefitElection)

      const hasSpouse: boolean = enrolledDependents.some((e) => e.type === BenefitDependentTypeEnum.Spouse)
      const hasChild: boolean = enrolledDependents.some((e) => e.type === BenefitDependentTypeEnum.Child)

      if (hasSpouse && hasChild) {
        return BenefitFamilyStatusEnum.EMPLOYEE_PLUS_FAMILY
      } else if (hasSpouse) {
        return BenefitFamilyStatusEnum.EMPLOYEE_PLUS_SPOUSE
      } else if (hasChild) {
        return BenefitFamilyStatusEnum.EMPLOYEE_PLUS_CHILDREN
      }

      return BenefitFamilyStatusEnum.EMPLOYEE_ONLY
    },

    getValidTierCostForNonVpcBenefitElectionAndBenefitPlan(
      plan: CompanyBenefitPlanDTO,
      nonVpcBenefitElection: NonVpcBenefitElection
    ): Optional<CompanyBenefitPlanTierCostDTO> {
      if (plan.vitable_only_in && plan.tier_costs.length === 1) {
        return plan.tier_costs[0] // Should always be Employee Plus Family
      }

      const familyStatusTierName: string = {
        EE: "Employee Only",
        ES: "Employee Plus Spouse",
        EC: "Employee Plus Children",
        EF: "Employee Plus Family"
      }[this.getValidFamilyStatusForNonVpcBenefitElection(nonVpcBenefitElection)]!

      return plan.tier_costs.find((tc) => tc.benefit_plan_tier_name === familyStatusTierName)
    },

    selectMedicareNonVpcBenefitPlan(
      employerAllowanceInCents: number | undefined,
      nonVpcBenefitElection: NonVpcBenefitElection
    ): void {
      nonVpcBenefitElection.selection = {
        type: "medicare",
        employerAllowanceInCents: employerAllowanceInCents
      }
    },

    selectExternalNonVpcBenefitPlan(
      externalPlan: MarketplacePlanDTO,
      employerAllowanceInCents: number | undefined,
      nonVpcBenefitElection: NonVpcBenefitElection
    ): void {
      nonVpcBenefitElection.selection = {
        type: "external",
        externalPlan: externalPlan,
        employerAllowanceInCents: employerAllowanceInCents
      }
    },

    clearNonVpcBenefitPlanSelection(nonVpcBenefitElection: NonVpcBenefitElection): void {
      nonVpcBenefitElection.selection = undefined
    },

    selectInternalNonVpcBenefitPlan(
      plan: CompanyBenefitPlanDTO,
      tierCost: CompanyBenefitPlanTierCostDTO,
      nonVpcBenefitElection: NonVpcBenefitElection
    ): void {
      nonVpcBenefitElection.selection = {
        type: "internal",
        benefitPlan: plan,
        tierCost: tierCost
      }
    },

    waiveNonVpcBenefitElection(nonVpcBenefitElection: NonVpcBenefitElection): void {
      nonVpcBenefitElection.selection = null
    },

    unwaiveNonVpcBenefitElection(
      nonVpcBenefitElection: NonVpcBenefitElection,
      priorSelection: NonVpcBenefitElection["selection"]
    ): void {
      nonVpcBenefitElection.selection = priorSelection
    },

    waiveVpcBenefitElection(): void {
      const vpcElection = this.activeFlowElectionsPackage?.vpcElection
      if (vpcElection) {
        vpcElection.waived = true
      }
    },

    unwaiveVpcBenefitElection(): void {
      const vpcElection = this.activeFlowElectionsPackage?.vpcElection
      if (vpcElection) {
        vpcElection.waived = false
      }
    },

    ensureVpcAutoIncluded(): void {
      const vpcElection = this.activeFlowElectionsPackage?.vpcElection
      if (!vpcElection) return
      if (vpcElection.waived) {
        vpcElection.waived = false
      }
    },

    isDependentEnrolledInVpcBenefitElection(dependent: BenefitElectionDependent): boolean {
      const vpcElection = this.activeFlowElectionsPackage?.vpcElection
      if (!vpcElection) return false
      return !(vpcElection.waivedDependentsIds ?? []).some((id) => id === dependent.legalDependentId)
    },

    electVpcBenefitElectionForDependent(dependent: BenefitElectionDependent, enroll: boolean): void {
      const vpcElection = this.activeFlowElectionsPackage?.vpcElection
      if (!vpcElection) return
      // Initialize the array in case this VpcBenefitElection was persisted before
      // waivedDependentsIds existed on the type.
      if (!vpcElection.waivedDependentsIds) vpcElection.waivedDependentsIds = []
      const index = vpcElection.waivedDependentsIds.findIndex((id) => id === dependent.legalDependentId)
      if (enroll) {
        if (index !== -1) {
          vpcElection.waivedDependentsIds.splice(index, 1)
        }
      } else {
        if (index === -1) {
          vpcElection.waivedDependentsIds.push(dependent.legalDependentId)
        }
      }
    }
  }
})
